import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const SCALE = [
  { v: 1, label: "Strongly Disagree" },
  { v: 2, label: "Disagree" },
  { v: 3, label: "Neutral" },
  { v: 4, label: "Agree" },
  { v: 5, label: "Strongly Agree" },
];

export default function Assessment() {
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const { user, refresh } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/assessment/questions").then((r) => setQuestions(r.data.questions));
  }, []);

  const onAnswer = (v) => {
    const next = [...answers];
    next[idx] = v;
    setAnswers(next);
    if (idx < questions.length - 1) {
      setTimeout(() => setIdx(idx + 1), 150);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/assessment/submit", { answers });
      setResult(data);
      if (user && typeof user === "object") refresh();
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20">
        <div className="bg-white border border-[#EAE5D9] rounded-2xl p-10 sm:p-14 text-center">
          <div className="text-xs uppercase tracking-[0.25em] text-[#8A857D]">Your Alignment Stage</div>
          <h1 className="font-display text-6xl text-[#A36B5E] italic mt-4" data-testid="assessment-result-stage">
            {result.stage}
          </h1>
          <div className="mt-3 text-sm text-[#5C5852]">
            Score: {result.score} / {result.max_score} ({result.percent}%)
          </div>
          <p className="mt-8 text-lg text-[#2D2A26] leading-relaxed max-w-2xl mx-auto">
            {result.description}
          </p>

          {/* Continuum */}
          <div className="mt-12 flex items-center justify-between max-w-md mx-auto text-xs">
            {["Seeking", "Awakening", "Aligned", "Anointed"].map((s) => (
              <div
                key={s}
                className={`px-3 py-1 rounded-full ${
                  s === result.stage ? "bg-[#A36B5E] text-white" : "text-[#8A857D]"
                }`}
              >
                {s}
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-wrap gap-3 justify-center">
            {user && typeof user === "object" ? (
              <button
                onClick={() => nav("/dashboard")}
                data-testid="assessment-go-dashboard"
                className="btn-primary rounded-full px-8 py-3 font-medium"
              >
                Go to your dashboard
              </button>
            ) : (
              <button
                onClick={() => nav("/register")}
                data-testid="assessment-go-register"
                className="btn-primary rounded-full px-8 py-3 font-medium"
              >
                Save your result · Start the course
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (questions.length === 0) {
    return <div className="py-32 text-center text-[#8A857D]">Loading the assessment...</div>;
  }

  const q = questions[idx];
  const progress = ((idx) / questions.length) * 100;
  const allAnswered = answers.length === questions.length && answers.every((a) => a);

  return (
    <div className="max-w-2xl mx-auto px-6 py-16">
      <div className="text-xs uppercase tracking-[0.25em] text-[#A36B5E] text-center">Divine Alignment Assessment</div>
      <h1 className="font-display text-3xl sm:text-4xl text-[#2D2A26] text-center mt-3 tracking-tight">
        Where are you on the continuum?
      </h1>

      {/* Progress */}
      <div className="mt-10 h-1 bg-[#EAE5D9] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#5F6F52] transition-all duration-500"
          style={{ width: `${progress}%` }}
          data-testid="assessment-progress"
        />
      </div>
      <div className="mt-2 text-xs text-[#8A857D] text-center">
        Question {idx + 1} of {questions.length}
      </div>

      <div className="mt-12 bg-white border border-[#EAE5D9] rounded-2xl p-8 sm:p-10">
        <div className="font-display text-2xl text-[#2D2A26] leading-snug" data-testid="assessment-question">
          {q.text}
        </div>

        <div className="mt-8 space-y-2">
          {SCALE.map((s) => (
            <button
              key={s.v}
              onClick={() => onAnswer(s.v)}
              data-testid={`assessment-option-${s.v}`}
              className={`w-full text-left px-5 py-4 rounded-xl border transition-all ${
                answers[idx] === s.v
                  ? "border-[#A36B5E] bg-[#A36B5E]/5 text-[#2D2A26]"
                  : "border-[#EAE5D9] hover:border-[#C49A7E] text-[#5C5852]"
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{s.label}</span>
                <span className="text-xs text-[#8A857D]">{s.v}</span>
              </div>
            </button>
          ))}
        </div>

        <div className="mt-8 flex justify-between">
          <button
            onClick={() => setIdx(Math.max(0, idx - 1))}
            disabled={idx === 0}
            className="text-sm text-[#5C5852] disabled:opacity-30"
            data-testid="assessment-prev"
          >
            ← Back
          </button>
          {idx === questions.length - 1 ? (
            <button
              onClick={submit}
              disabled={!allAnswered || submitting}
              data-testid="assessment-submit"
              className="btn-primary rounded-full px-6 py-3 text-sm font-medium disabled:opacity-50"
            >
              {submitting ? "Calculating..." : "See my result"}
            </button>
          ) : (
            <button
              onClick={() => setIdx(Math.min(questions.length - 1, idx + 1))}
              disabled={!answers[idx]}
              data-testid="assessment-next"
              className="text-sm text-[#A36B5E] disabled:opacity-30"
            >
              Next →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
